import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, Workflow, Lightbulb, HelpCircle } from 'lucide-react';

export const GuidesIndexPage: React.FC = () => {
  return (
    <DocsLayout
      title='Guides & Tutorials'
      description='Step-by-step guides, workflows, and tips to help you work more efficiently'
      breadcrumbs={[{ label: 'Documentation', path: '/docs' }, { label: 'Guides & Tutorials' }]}
    >
      <div className='max-w-[1000px]'>
        <section className='mb-20'>
          <div className='grid gap-6 md:grid-cols-2'>
            <Card>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <HelpCircle className='h-8 w-8 text-primary' />
                  <CardTitle className='text-lg font-semibold'>How Do I...?</CardTitle>
                </div>
                <CardDescription className='text-sm'>
                  Quick answers to common questions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Find fast answers to frequently asked questions organized by category.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <Workflow className='h-8 w-8 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Common Workflows</CardTitle>
                </div>
                <CardDescription className='text-sm'>
                  Real-world scenarios and solutions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Step-by-step workflows for typical daily tasks and scenarios.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <Lightbulb className='h-8 w-8 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Tips & Tricks</CardTitle>
                </div>
                <CardDescription className='text-sm'>Pro tips for power users</CardDescription>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Expert advice to help you work faster and more efficiently.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className='flex items-center gap-2'>
                  <BookOpen className='h-8 w-8 text-primary' />
                  <CardTitle className='text-lg font-semibold'>Feature Guides</CardTitle>
                </div>
                <CardDescription className='text-sm'>
                  Detailed feature documentation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  <Link to='/docs/features' className='text-primary hover:underline'>
                    Comprehensive guides
                  </Link>{' '}
                  for all Vehix features.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Quick Answers: How Do I...?</h2>

          <h3 className='text-lg font-bold mb-4'>Account & Access</h3>
          <ul className='mb-8 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Reset my password?</strong> Go to portal.vehix.ai and click "Forgot my
              password" on the sign-in screen
            </li>
            <li className='text-sm leading-6'>
              <strong>Update my profile?</strong> Click your avatar → Profile → Edit Profile
            </li>
            <li className='text-sm leading-6'>
              <strong>Change notification settings?</strong> Avatar → Notification Preferences
            </li>
          </ul>

          <h3 className='text-lg font-bold mb-4'>Finding Recalls</h3>
          <ul className='mb-8 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Search by VIN?</strong> Use Recall List → Enter VIN in search bar (full or
              last 6 digits work)
            </li>
            <li className='text-sm leading-6'>
              <strong>Find recalls in my area?</strong> Use Recall Map → Filter by ZIP Code + Radius
            </li>
            <li className='text-sm leading-6'>
              <strong>See only mobile-eligible recalls?</strong> Apply filter: Mobile Eligible = Yes
            </li>
            <li className='text-sm leading-6'>
              <strong>Find all recalls for a campaign?</strong> Use Campaign filter dropdown
            </li>
          </ul>

          <h3 className='text-lg font-bold mb-4'>Assigning Work</h3>
          <ul className='mb-8 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Assign a recall to a technician?</strong> Select recalls → Manual Routing →
              Choose technician → Set date → Confirm
            </li>
            <li className='text-sm leading-6'>
              <strong>Create a mobile route?</strong> Filter by location + Mobile Eligible → Select
              multiple → Batch Assign
            </li>
            <li className='text-sm leading-6'>
              <strong>Reassign a recall?</strong> Find recall → Click Reassign button → Choose new
              technician
            </li>
          </ul>

          <h3 className='text-lg font-bold mb-4'>Tracking & Monitoring</h3>
          <ul className='mb-8 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Check today's assignments?</strong> Route Tracker → View Today's routes
            </li>
            <li className='text-sm leading-6'>
              <strong>See completion rates?</strong> Route Tracker → Summary Dashboard shows
              completion percentage
            </li>
            <li className='text-sm leading-6'>
              <strong>Monitor technician progress?</strong> Route Tracker → Expand route → View
              individual recall status
            </li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Common Workflows</h2>

          <h3 className='text-lg font-bold mb-4'>Workflow 1: Customer Calls About Recall Notice</h3>
          <ol className='mb-8 space-y-2 list-decimal list-inside'>
            <li className='text-sm leading-6'>
              <strong>Step 1:</strong> Navigate to Recall List
            </li>
            <li className='text-sm leading-6'>
              <strong>Step 2:</strong> Enter customer's VIN or name in search
            </li>
            <li className='text-sm leading-6'>
              <strong>Step 3:</strong> Review recall details (campaign, description, parts
              availability)
            </li>
            <li className='text-sm leading-6'>
              <strong>Step 4:</strong> Check if mobile-eligible or requires dealer visit
            </li>
            <li className='text-sm leading-6'>
              <strong>Step 5:</strong> If unassigned, assign to available technician with
              appropriate date
            </li>
            <li className='text-sm leading-6'>
              <strong>Step 6:</strong> Inform customer of scheduled date/time
            </li>
          </ol>

          <h3 className='text-lg font-bold mb-4'>Workflow 2: Plan Mobile Routes for the Week</h3>
          <ol className='mb-8 space-y-2 list-decimal list-inside'>
            <li className='text-sm leading-6'>
              <strong>Monday AM:</strong> Review unassigned mobile-eligible recalls
            </li>
            <li className='text-sm leading-6'>
              <strong>Group by Zone:</strong> Use ZIP Code + Radius filters to create geographic
              clusters
            </li>
            <li className='text-sm leading-6'>
              <strong>Match Skills:</strong> Apply Skill Level filter matching available technicians
            </li>
            <li className='text-sm leading-6'>
              <strong>Batch Assign:</strong> Select 8-12 stops per day, assign to technician
            </li>
            <li className='text-sm leading-6'>
              <strong>Set Strategic Dates:</strong> Monday = North zone, Tuesday = East zone, etc.
            </li>
            <li className='text-sm leading-6'>
              <strong>Review & Optimize:</strong> Check map to ensure efficient routing
            </li>
          </ol>

          <h3 className='text-lg font-bold mb-4'>Workflow 3: Technician Running Behind Schedule</h3>
          <ol className='mb-8 space-y-2 list-decimal list-inside'>
            <li className='text-sm leading-6'>
              <strong>Check Status:</strong> Route Tracker → Find technician's route
            </li>
            <li className='text-sm leading-6'>
              <strong>Assess Progress:</strong> Review completed vs pending recalls
            </li>
            <li className='text-sm leading-6'>
              <strong>Identify Issue:</strong> Contact technician to understand delay
            </li>
            <li className='text-sm leading-6'>
              <strong>Make Decision:</strong>
              <ul className='ml-6 mt-2 space-y-1 list-disc list-inside'>
                <li className='text-sm leading-6'>
                  Minor delay? Inform remaining customers of new ETA
                </li>
                <li className='text-sm leading-6'>
                  Major delay? Reassign remaining stops to another technician
                </li>
              </ul>
            </li>
            <li className='text-sm leading-6'>
              <strong>Communicate:</strong> Update customers and reassigned technician
            </li>
            <li className='text-sm leading-6'>
              <strong>Document:</strong> Add notes to affected recalls
            </li>
          </ol>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Tips & Tricks</h2>

          <h3 className='text-lg font-bold mb-4'>Search & Filter Tips</h3>
          <ul className='mb-8 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Last 6 digits:</strong> Searching by last 6 VIN digits is faster than full VIN
            </li>
            <li className='text-sm leading-6'>
              <strong>Combine filters:</strong> Use ZIP + Radius + Mobile Eligible for optimal
              mobile route planning
            </li>
            <li className='text-sm leading-6'>
              <strong>Remember to clear:</strong> Filters persist - use "Clear All" button to reset
            </li>
            <li className='text-sm leading-6'>
              <strong>Save mental templates:</strong> Memorize your most-used filter combinations
            </li>
          </ul>

          <h3 className='text-lg font-bold mb-4'>Assignment Tips</h3>
          <ul className='mb-8 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Trust AI suggestions:</strong> The system's top 3 technician recommendations
              are usually accurate
            </li>
            <li className='text-sm leading-6'>
              <strong>Balance workload:</strong> Check technician assignment counts before assigning
            </li>
            <li className='text-sm leading-6'>
              <strong>Zone strategy:</strong> Assign geographic zones to specific days for
              efficiency
            </li>
            <li className='text-sm leading-6'>
              <strong>Add detailed notes:</strong> Include customer preferences, access
              instructions, parts info
            </li>
          </ul>

          <h3 className='text-lg font-bold mb-4'>Monitoring Tips</h3>
          <ul className='mb-8 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Morning routine:</strong> Check Route Tracker first thing to identify issues
              early
            </li>
            <li className='text-sm leading-6'>
              <strong>Proactive communication:</strong> Contact technicians showing red/delayed
              status
            </li>
            <li className='text-sm leading-6'>
              <strong>Set expectations:</strong> 8-12 stops/day is realistic for mobile service
            </li>
            <li className='text-sm leading-6'>
              <strong>Use date ranges:</strong> Compare Last 7 Days vs Last 30 Days to spot trends
            </li>
          </ul>

          <h3 className='text-lg font-bold mb-4'>Power User Tips</h3>
          <ul className='mb-8 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Keyboard shortcuts:</strong> Tab navigation is faster than mouse clicking
            </li>
            <li className='text-sm leading-6'>
              <strong>Batch operations:</strong> Select multiple recalls for faster assignment
            </li>
            <li className='text-sm leading-6'>
              <strong>Export data:</strong> Use CSV export for custom reporting and analysis
            </li>
            <li className='text-sm leading-6'>
              <strong>Dark mode:</strong> Enable for reduced eye strain during long sessions
            </li>
          </ul>
        </section>

        <section className='mb-20'>
          <h2 className='text-3xl font-bold mb-6'>Need More Help?</h2>
          <p className='text-sm leading-6'>
            Can't find what you're looking for? Check our{' '}
            <Link to='/docs/features' className='text-primary hover:underline'>
              feature guides
            </Link>{' '}
            for detailed information, or{' '}
            <Link to='/docs/support' className='text-primary hover:underline'>
              contact support
            </Link>{' '}
            for personalized assistance.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
};
